import { action, makeObservable, observable } from "mobx";
import { setupNetworkInterceptor } from "../api/setupNetworkInterceptor";

export type NetworkStatus = "online" | "offline" | "reconnected";

const RECONNECTED_VISIBLE_MS = 3000;
const PROBE_INTERVAL_MS = 5000;
const PROBE_TIMEOUT_MS = 5000;

// Resolves true when the API host answers at all. `no-cors` gives an opaque
// response, so CORS never makes a reachable server look offline — only a real
// network failure rejects.
const probeConnection = async (): Promise<boolean> => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;

  const target = process.env.REACT_APP_API_URL || window.location.origin;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(target, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

export class NetworkStatusStore {
  status: NetworkStatus = "online";

  private started = false;
  private probing = false;
  private reconnectedTimer: ReturnType<typeof setTimeout> | null = null;
  private probeTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    makeObservable(this, {
      status: observable,
      markOffline: action,
      markOnline: action,
      hideReconnected: action,
    });
  }

  get isVisible(): boolean {
    return this.status !== "online";
  }

  start = (): void => {
    if (this.started) return;
    this.started = true;

    window.addEventListener("offline", this.markOffline);
    window.addEventListener("online", this.handleBrowserOnline);
    setupNetworkInterceptor({
      onNetworkError: this.handleRequestNetworkError,
      onResponse: this.markOnline,
    });

    if (!navigator.onLine) this.markOffline();
  };

  markOffline = (): void => {
    this.clearReconnectedTimer();
    this.status = "offline";
    this.startProbing();
  };

  markOnline = (): void => {
    if (this.status !== "offline") return;
    this.stopProbing();
    this.status = "reconnected";
    this.reconnectedTimer = setTimeout(
      this.hideReconnected,
      RECONNECTED_VISIBLE_MS,
    );
  };

  hideReconnected = (): void => {
    this.reconnectedTimer = null;
    if (this.status === "reconnected") this.status = "online";
  };

  // The browser's `online` event only means a network interface is up, so
  // confirm the API is actually reachable before going green.
  private handleBrowserOnline = (): void => {
    void this.checkConnection();
  };

  // A single failed request can be a CORS or third-party issue, so confirm
  // before showing the banner.
  private handleRequestNetworkError = (): void => {
    if (this.status === "offline") return;
    void this.checkConnection();
  };

  private checkConnection = async (): Promise<void> => {
    if (this.probing) return;
    this.probing = true;
    try {
      const reachable = await probeConnection();
      if (reachable) {
        this.markOnline();
      } else if (this.status !== "offline") {
        this.markOffline();
      }
    } finally {
      this.probing = false;
    }
  };

  private startProbing = (): void => {
    if (this.probeTimer) return;
    this.probeTimer = setInterval(() => {
      void this.checkConnection();
    }, PROBE_INTERVAL_MS);
  };

  private stopProbing = (): void => {
    if (!this.probeTimer) return;
    clearInterval(this.probeTimer);
    this.probeTimer = null;
  };

  private clearReconnectedTimer = (): void => {
    if (!this.reconnectedTimer) return;
    clearTimeout(this.reconnectedTimer);
    this.reconnectedTimer = null;
  };
}

export const networkStatusStore = new NetworkStatusStore();
